import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { LANGUAGES, CURRENCIES, t as translate } from "@/i18n/translations";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AppCtx = createContext(null);
const LS = { lang: "ct01_lang", currency: "ct01_currency", cart: "ct01_cart", token: "ct01_token", country: "ct01_country", cookie: "ct01_cookie", wishlocal: "ct01_wishlist", customer: "ct01_customer" };

const EMPTY_CUSTOMER = { first_name: "", last_name: "", email: "", phone: "", street: "", apartment: "", postal: "", city: "", country: "CH" };

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(LS.lang) || "de");
  const [currency, setCurrency] = useState(() => localStorage.getItem(LS.currency) || "CHF");
  const [country, setCountry] = useState(() => localStorage.getItem(LS.country) || "CH");
  const [cart, setCart] = useState(() => { try { return JSON.parse(localStorage.getItem(LS.cart)) || []; } catch { return []; } });
  const [bookingCart, setBookingCart] = useState(() => { try { return JSON.parse(localStorage.getItem("ct01_booking_cart")) || []; } catch { return []; } });
  const [bookingCustomer, setBookingCustomerState] = useState(() => { try { return { ...EMPTY_CUSTOMER, ...(JSON.parse(localStorage.getItem(LS.customer)) || {}) }; } catch { return { ...EMPTY_CUSTOMER }; } });
  const [cartOpen, setCartOpen] = useState(false);
  const [rates, setRates] = useState({ CHF: 1 });
  const [token, setToken] = useState(() => localStorage.getItem(LS.token) || null);
  const [user, setUser] = useState(null);
  // `hydrating` is true while we're re-validating a persisted token against
  // /auth/me on initial page load. Route guards must wait for it before
  // deciding "not logged in", otherwise a page refresh throws the admin back
  // to /admin/login even though the JWT in localStorage is perfectly valid.
  const [hydrating, setHydrating] = useState(() => !!localStorage.getItem(LS.token));
  const [wishlist, setWishlist] = useState([]);
  const [cookieAccepted, setCookieAccepted] = useState(() => localStorage.getItem(LS.cookie));
  const [discount, setDiscount] = useState(null);

  const isRTL = LANGUAGES.find(l => l.code === lang)?.dir === "rtl";

  useEffect(() => { localStorage.setItem(LS.lang, lang); document.documentElement.lang = lang; document.documentElement.dir = isRTL ? "rtl" : "ltr"; }, [lang, isRTL]);
  useEffect(() => { localStorage.setItem(LS.currency, currency); }, [currency]);
  useEffect(() => { localStorage.setItem(LS.country, country); }, [country]);
  useEffect(() => { localStorage.setItem(LS.cart, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("ct01_booking_cart", JSON.stringify(bookingCart)); }, [bookingCart]);
  useEffect(() => { localStorage.setItem(LS.customer, JSON.stringify(bookingCustomer)); }, [bookingCustomer]);
  useEffect(() => { if (token) localStorage.setItem(LS.token, token); else localStorage.removeItem(LS.token); }, [token]);

  useEffect(() => { axios.get(`${API}/config`).then(r => setRates(r.data.rates || { CHF: 1 })).catch(() => {}); }, []);

  const authHeaders = useCallback(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  useEffect(() => {
    if (!token) { setUser(null); setWishlist([]); setHydrating(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setUser(r.data);
        // Autofill booking customer from profile — only fill blanks so we never overwrite typed data.
        setBookingCustomerState(prev => ({
          ...prev,
          first_name: prev.first_name || r.data.first_name || (r.data.name || "").split(" ")[0] || "",
          last_name:  prev.last_name  || r.data.last_name  || (r.data.name || "").split(" ").slice(1).join(" ") || "",
          email:      prev.email      || r.data.email     || "",
          phone:      prev.phone      || r.data.phone     || "",
          street:     prev.street     || r.data.street    || r.data.address || "",
          apartment:  prev.apartment  || r.data.apartment || "",
          postal:     prev.postal     || r.data.postal    || "",
          city:       prev.city       || r.data.city      || "",
          country:    prev.country    || r.data.country   || "CH",
        }));
      })
      .catch(() => setToken(null))
      .finally(() => setHydrating(false));
    axios.get(`${API}/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setWishlist(r.data.product_ids || []))
      .catch(() => {});
  }, [token]);

  const t = useCallback((key) => translate(lang, key), [lang]);

  const format = useCallback((chfPrice) => {
    const rate = rates[currency] ?? 1;
    const converted = Number(chfPrice || 0) * rate;
    const symbol = { CHF:"CHF ", EUR:"€", GBP:"£", USD:"$", SEK:"SEK ", DKK:"DKK ", NOK:"NOK ", PLN:"PLN ", CZK:"CZK ", TRY:"₺", IQD:"IQD " }[currency] || currency + " ";
    const decimals = ["IQD","TRY"].includes(currency) ? 0 : 2;
    return `${symbol}${converted.toFixed(decimals)}`;
  }, [currency, rates]);

  const track = useCallback((event, extra = {}) => {
    if (cookieAccepted !== "yes") return;
    axios.post(`${API}/analytics/track`, { event, ...extra }).catch(() => {});
  }, [cookieAccepted]);

  const addToCart = (product, quantity = 1, size = null, variant = null) => {
    setCart(prev => {
      const key = `${product.id}::${size||""}::${variant||""}`;
      const existing = prev.find(i => `${i.product_id}::${i.size||""}::${i.variant||""}` === key);
      if (existing) return prev.map(i => i === existing ? { ...i, quantity: i.quantity + quantity } : i);
      return [...prev, {
        product_id: product.id, slug: product.slug, name: product.name,
        price_chf: Number(product.sale_price_chf || product.price_chf),
        image: product.images?.[0] || "", quantity, size, variant,
      }];
    });
    setCartOpen(true);
    track("add_to_cart", { product_id: product.id, value_chf: Number(product.price_chf) });
  };

  const removeFromCart = (product_id, size, variant) =>
    setCart(prev => prev.filter(i => !(i.product_id === product_id && (i.size||"") === (size||"") && (i.variant||"") === (variant||""))));
  const updateQty = (product_id, size, variant, qty) => {
    if (qty <= 0) return removeFromCart(product_id, size, variant);
    setCart(prev => prev.map(i => (i.product_id === product_id && (i.size||"") === (size||"") && (i.variant||"") === (variant||"")) ? { ...i, quantity: qty } : i));
  };
  const clearCart = () => { setCart([]); setDiscount(null); };

  // ---- Booking cart (services scheduled in the salon) ----
  // Each item: { uid, service_id, name, variant, variant_label, price_chf, duration_min, is_tattoo,
  //              starts_at (set on schedule step) }
  const addBookingItem = (item) => {
    setBookingCart(prev => [...prev, { ...item, uid: `${Date.now()}-${Math.random().toString(36).slice(2,7)}`, starts_at: item.starts_at || null }]);
  };
  const removeBookingItem = (uid) => setBookingCart(prev => prev.filter(b => b.uid !== uid));
  const clearBookingCart = () => setBookingCart([]);
  // Assign contiguous start times starting at `firstIso`. Each service gets its own start
  // computed by adding the durations of the services that come before it.
  const scheduleBookingItems = (firstIso) => {
    setBookingCart(prev => {
      const base = new Date(firstIso);
      let cursor = base.getTime();
      return prev.map(b => {
        const startsAt = new Date(cursor).toISOString();
        const next = cursor + (Number(b.duration_min || 30) * 60 * 1000);
        cursor = next;
        return { ...b, starts_at: startsAt };
      });
    });
  };
  const setBookingCustomer = (data) => setBookingCustomerState(prev => ({ ...prev, ...data }));

  const bookingSubtotalCHF = bookingCart.reduce((s, b) => s + Number(b.price_chf || 0), 0);
  const bookingDepositCHF = Math.round(bookingSubtotalCHF * 50) / 100;
  const bookingCount = bookingCart.length;
  const bookingTotalDurationMin = bookingCart.reduce((s, b) => s + Number(b.duration_min || 30), 0);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotalCHF = cart.reduce((s, i) => s + i.price_chf * i.quantity, 0);
  const cartDiscountCHF = discount?.amount_chf || 0;

  const applyDiscount = async (code, subtotal_chf, email = null) => {
    try {
      const { data } = await axios.post(`${API}/discounts/validate`, { code, subtotal_chf, email });
      setDiscount(data);
      return { ok: true, data };
    } catch (e) {
      setDiscount(null);
      return { ok: false, error: e.response?.data?.detail || "invalid" };
    }
  };
  const clearDiscount = () => setDiscount(null);

  const toggleWishlist = async (product_id) => {
    if (!token) return { needLogin: true };
    const { data } = await axios.post(`${API}/wishlist/toggle`, { product_id }, { headers: authHeaders() });
    setWishlist(prev => data.in_wishlist ? [...new Set([...prev, product_id])] : prev.filter(x => x !== product_id));
    return data;
  };
  const inWishlist = (product_id) => wishlist.includes(product_id);

  const login = (tok, u) => { setToken(tok); setUser(u); setHydrating(false); };
  const logout = () => { setToken(null); setUser(null); setWishlist([]); setHydrating(false); };

  const acceptCookies = (yes) => { localStorage.setItem(LS.cookie, yes ? "yes" : "no"); setCookieAccepted(yes ? "yes" : "no"); };

  const value = useMemo(() => ({
    lang, setLang, currency, setCurrency, country, setCountry, isRTL,
    cart, cartOpen, setCartOpen, addToCart, removeFromCart, updateQty, clearCart,
    bookingCart, addBookingItem, removeBookingItem, clearBookingCart, scheduleBookingItems,
    bookingCustomer, setBookingCustomer,
    bookingSubtotalCHF, bookingDepositCHF, bookingCount, bookingTotalDurationMin,
    cartCount, cartSubtotalCHF, cartDiscountCHF, discount, applyDiscount, clearDiscount, format, rates,
    token, user, hydrating, login, logout, authHeaders, t, languages: LANGUAGES, currencies: CURRENCIES,
    wishlist, toggleWishlist, inWishlist,
    cookieAccepted, acceptCookies, track,
  }), [lang, currency, country, isRTL, cart, bookingCart, bookingCustomer, cartOpen, rates, token, user, hydrating, discount, wishlist, cookieAccepted, format, t]);

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export function useApp() {
  const v = useContext(AppCtx);
  if (!v) throw new Error("useApp must be used inside AppProvider");
  return v;
}
